import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  notificationService,
  NotificationItem,
} from "../services/notifications/notification.service";
import { playAppAudio, preloadAudio } from "./audioManager";
import newMessageNotificationSound from "../assets/audio/new_message_notification.mp3";

// Preload notification sound into Web Audio cache on startup for mobile responsiveness
if (typeof window !== "undefined") {
  preloadAudio(newMessageNotificationSound).catch(() => {});
}

export interface NotificationState {
  unreadCount: number;
  notifications: NotificationItem[];
  totalCount: number;
  currentPage: number;
  isLoading: boolean;
  hasInitialized: boolean;
  lastFetchedAt: number;

  fetchUnreadCount: () => Promise<void>;
  fetchNotifications: (page?: number) => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
  decrementUnreadCount: () => void;
  resetNotifications: () => void;
}

let pollingInFlight = false;

export const useNotificationStore = create(
  persist<NotificationState>(
    (set, get) => ({
      unreadCount: 0,
      notifications: [],
      totalCount: 0,
      currentPage: 1,
      isLoading: false,
      hasInitialized: false,
      lastFetchedAt: 0,

      fetchUnreadCount: async () => {
        // Prevent concurrent requests or fetches within 3 seconds
        const now = Date.now();
        if (pollingInFlight || now - get().lastFetchedAt < 3000) {
          return;
        }

        try {
          pollingInFlight = true;
          const prevCount = get().unreadCount;
          const isInitialized = get().hasInitialized;

          const res = await notificationService.getUnreadCount();
          const newCount = res.unread_count ?? 0;

          // Trigger audio chime only on new incoming messages, not during initial hydration
          if (isInitialized && newCount > prevCount) {
            try {
              playAppAudio(newMessageNotificationSound);
            } catch (err) {
              console.warn("Audio chime playback error:", err);
            }
          }

          set({
            unreadCount: newCount,
            hasInitialized: true,
            lastFetchedAt: Date.now(),
          });
        } catch (error) {
          console.error("Failed to fetch notification unread count:", error);
        } finally {
          pollingInFlight = false;
        }
      },

      fetchNotifications: async (page = 1) => {
        try {
          set({ isLoading: true });
          const res = await notificationService.getNotifications(page, 20);
          set({
            notifications: res.notifications,
            totalCount: res.total_count,
            unreadCount: res.unread_count,
            currentPage: page,
            isLoading: false,
            lastFetchedAt: Date.now(),
          });
        } catch (error) {
          console.error("Failed to fetch notifications list:", error);
          set({ isLoading: false });
        }
      },

      markAsRead: async (id: number) => {
        const prevNotifications = get().notifications;
        const prevUnreadCount = get().unreadCount;

        const target = prevNotifications.find((n) => n.id === id);
        if (!target || target.is_read) {
          return;
        }

        // Optimistic update
        set({
          unreadCount: Math.max(0, prevUnreadCount - 1),
          notifications: prevNotifications.map((n) =>
            n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
          ),
        });

        try {
          await notificationService.markAsRead(id);
        } catch (error) {
          console.error(`Failed to mark notification ${id} as read, rolling back:`, error);
          set({ notifications: prevNotifications, unreadCount: prevUnreadCount });
        }
      },

      markAllAsRead: async () => {
        const prevNotifications = get().notifications;
        const prevUnreadCount = get().unreadCount;

        if (prevUnreadCount === 0) {
          return;
        }

        // Optimistic update
        const nowIso = new Date().toISOString();
        set({
          unreadCount: 0,
          notifications: prevNotifications.map((n) => ({
            ...n,
            is_read: true,
            read_at: n.read_at || nowIso,
          })),
        });

        try {
          await notificationService.markAllAsRead();
        } catch (error) {
          console.error("Failed to mark all notifications as read, rolling back:", error);
          set({ notifications: prevNotifications, unreadCount: prevUnreadCount });
        }
      },

      deleteNotification: async (id: number) => {
        const prevNotifications = get().notifications;
        const prevUnreadCount = get().unreadCount;

        const target = prevNotifications.find((n) => n.id === id);
        const wasUnread = target && !target.is_read;

        // Optimistic update
        set({
          notifications: prevNotifications.filter((n) => n.id !== id),
          totalCount: Math.max(0, get().totalCount - 1),
          unreadCount: wasUnread ? Math.max(0, prevUnreadCount - 1) : prevUnreadCount,
        });

        try {
          await notificationService.deleteNotification(id);
        } catch (error) {
          console.error(`Failed to delete notification ${id}, rolling back:`, error);
          set({ notifications: prevNotifications, unreadCount: prevUnreadCount });
        }
      },

      decrementUnreadCount: () => {
        set((state) => ({
          unreadCount: Math.max(0, state.unreadCount - 1),
        }));
      },

      resetNotifications: () => {
        set({
          unreadCount: 0,
          notifications: [],
          totalCount: 0,
          currentPage: 1,
          isLoading: false,
          hasInitialized: false,
          lastFetchedAt: 0,
        });
      },
    }),
    {
      name: "shoonit-notifications",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ unreadCount: state.unreadCount }),
    }
  )
);
